import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { barcodeSchema, trackingNoSchema } from '@ecobox/schema';
import { newOpsId } from '@ecobox/firebase';
import {
  Button,
  Card,
  Field,
  Input,
  ScanInput,
  ScannerView,
  Select,
  Textarea,
  scanFeedback,
  useToast,
} from '@ecobox/ui';
import {
  useExportContainers,
  useImportContainers,
  useInboundTrips,
  useLoadingTrips,
} from '../hooks/useOps';
import { containerLabel, tripLabel } from '../labels';
import { loadBarcodeDetector } from '../scanner/detector';
import { useBranch, useServices } from '../services';

export const EXCEPTION_CHOICES = [
  { type: 'damaged', label: 'A box is damaged' },
  { type: 'missing', label: 'A box is missing' },
  { type: 'overage', label: 'An extra box that should not be here' },
  { type: 'wrong_destination', label: 'A box for another destination' },
  { type: 'illegal_scan', label: 'A box is in the wrong place' },
  { type: 'customs', label: 'A customs problem' },
  { type: 'other', label: 'Something else' },
] as const;

type SubjectKind = 'box' | 'shipment' | 'trip' | 'container';
const KINDS: readonly { kind: SubjectKind; label: string }[] = [
  { kind: 'box', label: 'A box' },
  { kind: 'shipment', label: 'A shipment' },
  { kind: 'trip', label: 'A trip' },
  { kind: 'container', label: 'A container' },
];

function isKind(v: string | null): v is SubjectKind {
  return v === 'box' || v === 'shipment' || v === 'trip' || v === 'container';
}

/** Small previews of the pictures taken, until the report is sent. */
function usePreviews(files: readonly File[]): string[] {
  const urls = useMemo(
    () =>
      files.map((f) => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(f) : '')),
    [files],
  );
  useEffect(
    () => () => {
      for (const u of urls)
        if (u && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(u);
    },
    [urls],
  );
  return urls;
}

export function RaiseExceptionPage() {
  const { writer, uploads } = useServices();
  const branch = useBranch().current;
  const toast = useToast();
  const [params] = useSearchParams();
  const presetKind = params.get('kind');

  const [type, setType] = useState<(typeof EXCEPTION_CHOICES)[number]['type']>('damaged');
  const [kind, setKind] = useState<SubjectKind>(isKind(presetKind) ? presetKind : 'box');
  const [subjectId, setSubjectId] = useState(params.get('id') ?? '');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [camera, setCamera] = useState(false);
  const [exceptionId, setExceptionId] = useState(() => newOpsId('exc'));
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const previews = usePreviews(photos);

  const loadingTrips = useLoadingTrips(kind === 'trip' ? branch : null);
  const inboundTrips = useInboundTrips(kind === 'trip' ? branch : null);
  const exportContainers = useExportContainers(kind === 'container' ? branch : null);
  const importContainers = useImportContainers(kind === 'container' ? branch : null);
  const trips = [...loadingTrips.data, ...inboundTrips.data];
  const containers = [...exportContainers.data, ...importContainers.data];

  const submit = async () => {
    setError(null);
    const id = subjectId.trim().toUpperCase();
    if (kind === 'box' && !barcodeSchema.safeParse(id).success) {
      return setError('Scan or type a box barcode like EB0000012344.');
    }
    if (kind === 'shipment' && !trackingNoSchema.safeParse(id).success) {
      return setError('Type a tracking number like RUH260042171.');
    }
    if ((kind === 'trip' || kind === 'container') && subjectId === '') {
      return setError(`Choose the ${kind}.`);
    }
    if (description.trim() === '') return setError('Say what happened.');
    setBusy(true);
    try {
      // The pictures are shrunk and saved on the phone at once and upload in the background, so
      // the report goes now even with no signal. The command names where each one will be.
      const paths: string[] = [];
      for (const photo of photos)
        paths.push(await uploads.enqueueUpload(photo, { kind: 'exception' }));
      writer.send('raiseException', {
        exceptionId,
        type,
        subject: { kind, id: kind === 'trip' || kind === 'container' ? subjectId : id },
        description: description.trim().slice(0, 1000),
        photos: paths,
      });
      setSent(true);
      toast.show({
        title: 'Problem reported',
        message:
          paths.length > 0
            ? 'Saved. It and its photos send when you have signal.'
            : 'Saved. It sends when you have signal.',
        tone: 'success',
      });
      setExceptionId(newOpsId('exc'));
      setDescription('');
      setPhotos([]);
      setSubjectId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ops-stack">
      <h1 className="ops-title">Report a problem</h1>
      {sent ? (
        <p role="status" className="ops-ok" style={{ margin: 0 }}>
          Reported. A person at the branch will pick it up. You can report another.
        </p>
      ) : null}
      <Card>
        <div className="ops-stack">
          <div data-tour="problem-type">
            <Field label="What is wrong?">
              {(p) => (
                <Select
                  {...p}
                  size="touch"
                  value={type}
                  onChange={(e) => setType(e.target.value as typeof type)}
                >
                  {EXCEPTION_CHOICES.map((c) => (
                    <option key={c.type} value={c.type}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div data-tour="problem-subject">
            <Field label="It is about">
              {(p) => (
                <Select
                  {...p}
                  size="touch"
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value as SubjectKind);
                    setSubjectId('');
                  }}
                >
                  {KINDS.map((k) => (
                    <option key={k.kind} value={k.kind}>
                      {k.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          {kind === 'box' ? (
            <>
              <div data-tour="problem-box">
                <ScanInput
                  label="Box barcode"
                  placeholder="Scan or type the box barcode"
                  autoFocus={false}
                  keepFocus={false}
                  onScan={(code) => {
                    setSubjectId(code.toUpperCase());
                    scanFeedback('ok');
                  }}
                />
              </div>
              <Button size="touch" variant="secondary" block onClick={() => setCamera(true)}>
                Scan with the camera
              </Button>
              {subjectId ? <div className="ops-code">{subjectId}</div> : null}
            </>
          ) : null}
          {kind === 'shipment' ? (
            <Field label="Tracking number">
              {(p) => (
                <Input
                  {...p}
                  size="touch"
                  value={subjectId}
                  maxLength={14}
                  onChange={(e) => setSubjectId(e.target.value.toUpperCase())}
                />
              )}
            </Field>
          ) : null}
          {kind === 'trip' ? (
            <Field label="Which trip?">
              {(p) => (
                <Select
                  {...p}
                  size="touch"
                  value={subjectId}
                  onChange={(e) => setSubjectId(e.target.value)}
                >
                  <option value="">Choose a trip</option>
                  {trips.map((t) => (
                    <option key={t.id} value={t.id}>
                      {tripLabel(t)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}
          {kind === 'container' ? (
            <Field label="Which container?">
              {(p) => (
                <Select
                  {...p}
                  size="touch"
                  value={subjectId}
                  onChange={(e) => setSubjectId(e.target.value)}
                >
                  <option value="">Choose a container</option>
                  {containers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {containerLabel(c)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          <div data-tour="problem-description">
            <Field label="What happened?" required>
              {(p) => (
                <Textarea
                  {...p}
                  rows={4}
                  value={description}
                  maxLength={900}
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
            </Field>
          </div>

          <div data-tour="problem-photos">
            <Field
              label="Photos"
              hint="Up to 10. They are made smaller and upload when you have signal; the report does not wait for them."
            >
              {(p) => (
                <input
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  className="ops-file"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={(e) =>
                    setPhotos([...photos, ...Array.from(e.target.files ?? [])].slice(0, 10))
                  }
                />
              )}
            </Field>
          </div>
          {previews.length > 0 ? (
            <div className="ops-thumbs" aria-label="Photos taken">
              {previews.map((src, i) => (
                <img key={src || i} src={src} alt={`Photo ${i + 1}`} className="ops-thumb" />
              ))}
            </div>
          ) : null}

          {error ? (
            <p role="alert" className="ops-error" style={{ margin: 0 }}>
              {error}
            </p>
          ) : null}
          <Button
            size="touch"
            variant="cta"
            block
            loading={busy}
            data-tour="problem-send"
            onClick={() => void submit()}
          >
            Report the problem
          </Button>
        </div>
      </Card>
      {camera ? (
        <ScannerView
          loadDetector={loadBarcodeDetector}
          title="Scan the box"
          onRead={(code) => {
            setSubjectId(code.toUpperCase());
            setCamera(false);
            return 'ok';
          }}
          onClose={() => setCamera(false)}
        />
      ) : null}
    </div>
  );
}
